import type {
  CoPilotTraceExportPayload,
  CoPilotTraceTimelineEntry,
} from './co-pilot-trace-export';
import {
  CO_PILOT_TRANSCRIPT_ASSISTANT_LABEL,
  buildCoPilotTranscriptFilename,
  formatCoPilotTranscriptHeader,
  resolveCoPilotTranscriptDisplayTitle,
} from './co-pilot-transcript-export';
import {
  sanitizeCoPilotExportMarkdownCodeContent,
  sanitizeCoPilotExportMarkdownPlainText,
  sanitizeCoPilotExportText,
} from './co-pilot-export-sanitize';

const SESSION_MARKDOWN_TIMELINE_KINDS = new Set<
  CoPilotTraceTimelineEntry['kind']
>(['user_message', 'assistant_message', 'http_request']);

function escapeMarkdownFenceContent(value: string): string {
  return value.replace(/```/g, '`\u200b``');
}

function wrapMarkdownFence({
  language,
  content,
}: {
  language: string;
  content: string;
}): string {
  const trimmed = sanitizeCoPilotExportMarkdownCodeContent(content).trim();
  if (!trimmed) {
    return '';
  }
  return `\`\`\`${language}\n${escapeMarkdownFenceContent(trimmed)}\n\`\`\``;
}

function formatStatusLine({
  status,
  durationMs,
  timestamp,
}: {
  status?: string;
  durationMs?: number;
  timestamp?: string;
}): string {
  const parts: string[] = [];
  if (status) {
    parts.push(`Status: ${sanitizeCoPilotExportText(status)}`);
  }
  if (typeof durationMs === 'number') {
    parts.push(`Duration: ${durationMs}ms`);
  }
  if (timestamp) {
    parts.push(`Time: ${sanitizeCoPilotExportText(timestamp)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : '';
}

function formatJsonRecord(value: Record<string, unknown> | undefined): string {
  if (!value || Object.keys(value).length === 0) {
    return '';
  }
  return wrapMarkdownFence({
    language: 'json',
    content: JSON.stringify(value, null, 2),
  });
}

function sanitizeMarkdownLabel(value: string): string {
  return sanitizeCoPilotExportMarkdownPlainText(value);
}

function formatSessionTimelineEntryMarkdown(
  entry: CoPilotTraceTimelineEntry,
): string | null {
  switch (entry.kind) {
    case 'user_message': {
      const text = sanitizeMarkdownLabel(entry.text.trim());
      return text ? `## User\n\n${text}` : null;
    }
    case 'assistant_message': {
      const text = sanitizeMarkdownLabel(entry.text.trim());
      return text
        ? `## ${CO_PILOT_TRANSCRIPT_ASSISTANT_LABEL}\n\n${text}`
        : null;
    }
    case 'http_request': {
      const statusLine = formatStatusLine({
        status: entry.status,
        durationMs: entry.durationMs,
        timestamp: entry.timestamp,
      });
      const sections = [`### ${sanitizeMarkdownLabel(entry.displayName)}`];
      if (statusLine) {
        sections.push('', statusLine);
      }
      const requestBlock = entry.request
        ? wrapMarkdownFence({ language: 'http', content: entry.request })
        : '';
      const responseBlock = entry.response
        ? wrapMarkdownFence({ language: 'http', content: entry.response })
        : '';
      if (requestBlock) {
        sections.push('', '#### Request', '', requestBlock);
      }
      if (responseBlock) {
        sections.push('', '#### Response', '', responseBlock);
      }
      if (entry.error?.trim()) {
        sections.push(
          '',
          `**Error:** ${sanitizeMarkdownLabel(entry.error.trim())}`,
        );
      }
      return sections.join('\n');
    }
    case 'tool': {
      if (entry.labelOnly) {
        return `### ${sanitizeMarkdownLabel(entry.displayName)}`;
      }
      const statusLine = formatStatusLine({
        status: entry.status,
        durationMs: entry.durationMs,
        timestamp: entry.timestamp,
      });
      const sections = [`### ${sanitizeMarkdownLabel(entry.displayName)}`];
      if (entry.toolName) {
        sections.push(`Tool: \`${sanitizeCoPilotExportText(entry.toolName)}\``);
      }
      if (statusLine) {
        sections.push(statusLine);
      }
      const argsBlock = formatJsonRecord(entry.args);
      if (argsBlock) {
        sections.push('', '#### Input', '', argsBlock);
      }
      if (entry.output?.trim()) {
        sections.push(
          '',
          '#### Output',
          '',
          wrapMarkdownFence({ language: 'text', content: entry.output }),
        );
      }
      if (entry.error?.trim()) {
        sections.push(
          '',
          `**Error:** ${sanitizeMarkdownLabel(entry.error.trim())}`,
        );
      }
      return sections.join('\n');
    }
    default:
      return null;
  }
}

export function countRenderableSessionMarkdownSections({
  exportPayload,
}: {
  exportPayload: CoPilotTraceExportPayload;
}): number {
  return filterSessionMarkdownTimeline(exportPayload.timeline)
    .map((entry) => formatSessionTimelineEntryMarkdown(entry))
    .filter((section): section is string => Boolean(section?.trim())).length;
}

export function filterSessionMarkdownTimeline(
  timeline: CoPilotTraceTimelineEntry[],
): CoPilotTraceTimelineEntry[] {
  return timeline.filter((entry) =>
    SESSION_MARKDOWN_TIMELINE_KINDS.has(entry.kind),
  );
}

export function buildCoPilotSessionMarkdown({
  title,
  threadId,
  exportPayload,
  timeZone,
}: {
  title: string | null | undefined;
  threadId: string;
  exportPayload: CoPilotTraceExportPayload;
  timeZone?: string;
}): string {
  const displayTitle = sanitizeCoPilotExportMarkdownPlainText(
    resolveCoPilotTranscriptDisplayTitle({ title }) ?? 'Co-pilot chat',
  );
  const header = formatCoPilotTranscriptHeader({
    title: displayTitle,
    threadId,
    exportedAt: exportPayload.exportedAt,
    timeZone,
  });
  const sections = filterSessionMarkdownTimeline(exportPayload.timeline)
    .map((entry) => formatSessionTimelineEntryMarkdown(entry))
    .filter((section): section is string => Boolean(section));

  if (sections.length === 0) {
    return `${header}\n`;
  }

  return `${header}\n\n${sections.join('\n\n')}\n`;
}

export function buildCoPilotSessionMarkdownExport({
  title,
  threadId,
  exportPayload,
  timeZone,
}: {
  title: string | null | undefined;
  threadId: string;
  exportPayload: CoPilotTraceExportPayload;
  timeZone?: string;
}): { markdown: string; filename: string } {
  const displayTitle = sanitizeCoPilotExportMarkdownPlainText(
    resolveCoPilotTranscriptDisplayTitle({ title }) ?? 'Co-pilot chat',
  );
  return {
    markdown: buildCoPilotSessionMarkdown({
      title: displayTitle,
      threadId,
      exportPayload,
      timeZone,
    }),
    filename: buildCoPilotTranscriptFilename(displayTitle),
  };
}

export function countCoPilotSessionMarkdownSections(markdown: string): number {
  return markdown.split('\n').filter((line) => line.startsWith('### ')).length;
}

export const buildCoPilotTraceMarkdownExport =
  buildCoPilotSessionMarkdownExport;
export const buildCoPilotTraceMarkdown = buildCoPilotSessionMarkdown;
export const countCoPilotTraceMarkdownSections =
  countCoPilotSessionMarkdownSections;
